import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Check, ChevronLeft, PartyPopper } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useConversion } from "@/context/ConversionContext";
import { postJSON } from "@/lib/api";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SmartFormModal() {
  const { t, to, lang } = useLang();
  const { formOpen, closeForm, formPrefill } = useConversion();
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    profile: "", need: "", firstName: "", lastName: "", postalCode: "",
    email: "", phone: "", contactPreference: "email", message: "",
    consent: false, newsletter: false, company: "",
  });

  useEffect(() => {
    if (formOpen) {
      setStep(1);
      setStatus("idle");
      setErrors({});
      setData((d) => ({
        ...d,
        profile: formPrefill?.profile || "",
        need: formPrefill?.need || "",
      }));
      document.body.style.overflow = "hidden";
    }
    return () => { document.body.style.overflow = ""; };
  }, [formOpen, formPrefill]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeForm();
    if (formOpen) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [formOpen, closeForm]);

  if (!formOpen) return null;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => {
    if (step === 1 && !data.profile) return setErrors({ profile: t.form.selectProfile });
    if (step === 2 && !data.need) return setErrors({ need: t.form.selectNeed });
    setErrors({});
    setStep((s) => Math.min(3, s + 1));
  };

  const validateFinal = () => {
    const e = {};
    if (!data.firstName.trim()) e.firstName = t.form.required;
    if (!data.lastName.trim()) e.lastName = t.form.required;
    if (!data.email.trim()) e.email = t.form.required;
    else if (!emailRe.test(data.email)) e.email = t.form.invalidEmail;
    if (!data.consent) e.consent = t.form.consentRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateFinal()) return;
    setStatus("loading");
    try {
      await postJSON("/leads", { ...data, source: "smart-form", lang });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setErrors({ global: err.message || t.form.errorGeneric });
    }
  };

  const pct = status === "success" ? 100 : (step / 3) * 100;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label={t.form.title} data-testid="smart-form-modal">
      <div className="absolute inset-0 bg-nek-navy/50 backdrop-blur-sm" onClick={closeForm} />
      <div className="relative z-10 flex max-h-[94vh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white shadow-lift">
        <div className="flex items-start justify-between gap-4 border-b border-nek-border px-6 py-4">
          <div>
            <h2 className="text-lg font-heading font-bold text-nek-navy">{t.form.title}</h2>
            <p className="text-sm text-nek-gray">{t.form.subtitle}</p>
          </div>
          <button type="button" onClick={closeForm} aria-label={t.cta.close} data-testid="form-close" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-nek-gray hover:bg-nek-light">
            <X className="h-5 w-5" />
          </button>
        </div>

        {status !== "success" && (
          <div className="px-6 pt-4">
            <div className="flex items-center justify-between text-xs font-semibold text-nek-gray">
              <span>{t.form.step} {step} {t.form.of} 3</span>
              <span>{[t.form.step1Title, t.form.step2Title, t.form.step3Title][step - 1]}</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-nek-light">
              <div className="h-full rounded-full bg-nek-blue transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto px-6 py-5">
          {status === "success" ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center" data-testid="form-success">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-nek-green/15 text-nek-green">
                <PartyPopper className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-heading font-bold text-nek-navy">{t.form.successTitle}</h3>
              <p className="max-w-sm text-nek-gray">{t.form.successText}</p>
              <button type="button" onClick={closeForm} className="btn-navy mt-2">{t.cta.close}</button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" value={data.company} onChange={(e) => set("company", e.target.value)} className="hidden" />

              {step === 1 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {t.form.profiles.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { set("profile", p.label); setErrors({}); }}
                      data-testid={`form-profile-${p.id}`}
                      className={`rounded-xl border p-4 text-left font-heading font-semibold transition-all ${
                        data.profile === p.label ? "border-nek-blue bg-nek-blue/5 text-nek-navy ring-2 ring-nek-blue/30" : "border-nek-border text-nek-ink hover:border-nek-blue"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                  {errors.profile && <p className="col-span-full text-sm text-red-600">{errors.profile}</p>}
                </div>
              )}

              {step === 2 && (
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {t.form.needs.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => { set("need", n); setErrors({}); }}
                      data-testid={`form-need-${n}`}
                      className={`rounded-lg border px-4 py-3 text-left text-sm font-medium transition-all ${
                        data.need === n ? "border-nek-blue bg-nek-blue/5 text-nek-navy ring-2 ring-nek-blue/30" : "border-nek-border text-nek-ink hover:border-nek-blue"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  {errors.need && <p className="col-span-full text-sm text-red-600">{errors.need}</p>}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label={t.form.firstName} error={errors.firstName}>
                      <input data-testid="form-firstname" className="nek-input" value={data.firstName} onChange={(e) => set("firstName", e.target.value)} />
                    </Field>
                    <Field label={t.form.lastName} error={errors.lastName}>
                      <input data-testid="form-lastname" className="nek-input" value={data.lastName} onChange={(e) => set("lastName", e.target.value)} />
                    </Field>
                    <Field label={t.form.postalCode}>
                      <input data-testid="form-postal" className="nek-input" value={data.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
                    </Field>
                    <Field label={t.form.phone}>
                      <input data-testid="form-phone" type="tel" className="nek-input" value={data.phone} onChange={(e) => set("phone", e.target.value)} />
                    </Field>
                    <Field label={t.form.email} error={errors.email} className="sm:col-span-2">
                      <input data-testid="form-email" type="email" className="nek-input" value={data.email} onChange={(e) => set("email", e.target.value)} />
                    </Field>
                  </div>

                  <Field label={t.form.contactPref}>
                    <div className="flex flex-wrap gap-2">
                      {t.form.contactPrefOptions.map((o) => (
                        <button key={o.id} type="button" onClick={() => set("contactPreference", o.id)}
                          className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${data.contactPreference === o.id ? "border-nek-blue bg-nek-blue/5 text-nek-navy" : "border-nek-border text-nek-ink hover:border-nek-blue"}`}>
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label={t.form.message}>
                    <textarea data-testid="form-message" rows={3} className="nek-input resize-none" placeholder={t.form.messagePlaceholder} value={data.message} onChange={(e) => set("message", e.target.value)} />
                  </Field>

                  <div className="rounded-xl bg-nek-light p-4">
                    <p className="text-xs text-nek-gray">{t.form.rgpd} <Link to={to("/politique-confidentialite")} className="font-semibold text-nek-blue underline" onClick={closeForm}>{t.form.privacyLink}</Link></p>
                    <label className="mt-3 flex items-start gap-2.5 text-sm text-nek-ink">
                      <input type="checkbox" data-testid="form-consent" checked={data.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{t.form.consent}</span>
                    </label>
                    {errors.consent && <p className="ml-6 mt-1 text-sm text-red-600">{errors.consent}</p>}
                    <label className="mt-2.5 flex items-start gap-2.5 text-sm text-nek-ink">
                      <input type="checkbox" data-testid="form-newsletter" checked={data.newsletter} onChange={(e) => set("newsletter", e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{t.form.newsletter}</span>
                    </label>
                  </div>
                  {errors.global && <p className="text-sm text-red-600" data-testid="form-global-error">{errors.global}</p>}
                </div>
              )}

              <div className="mt-6 flex items-center justify-between gap-3">
                {step > 1 ? (
                  <button type="button" onClick={() => setStep((s) => s - 1)} data-testid="form-back" className="btn-outline !py-3">
                    <ChevronLeft className="h-4 w-4" /> {t.cta.back}
                  </button>
                ) : <span />}
                {step < 3 ? (
                  <button type="button" onClick={next} data-testid="form-next" className="btn-coral !py-3">{t.cta.next}</button>
                ) : (
                  <button type="submit" disabled={status === "loading"} data-testid="form-submit" className="btn-coral !py-3">
                    {status === "loading" ? t.form.submitting : <><Check className="h-4 w-4" /> {t.cta.send}</>}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 block text-sm font-medium text-nek-ink">{label}</span>
      {children}
      {error && <span className="mt-1 block text-sm text-red-600">{error}</span>}
    </label>
  );
}
